import React, {useState, useCallback, useMemo} from 'react';
import {IconButton, styled} from '@mui/material';
import {
  Body,
  Button,
  Headline,
  Icon,
  Modal,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  isCampaignSegment,
  SEGMENT_SCOPE,
  SEGMENT_SCOPE_SHORT_LABEL,
  type Segment,
} from '@/types/models/segments/segment';
import AnimatedCaret from '@/components/animated-caret';
import Dropdown, {type DropdownOption} from '@/components/dropdown';
import {useDeleteSegmentMutation} from '@/hooks/mutations/segments';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './SegmentsButton.module.scss';

export type SegmentsButtonProps = {
  segments: Segment[];
  activeSegment: Segment | null;
  /** When provided (e.g. Creator CRM), the popper marks this campaign segment as active. */
  activeCampaignSegment?: Segment | null;
  isLoadingSegments: boolean;
  /** Called when a segment is deleted; use to clear filters if the deleted segment was active. */
  onSegmentDeleted?: (segment: Segment) => void;
  onSelectSegment: (segment: Segment) => void;
  /** Called when the segments button is clicked to open the dropdown. */
  onOpen?: () => void;
};

const StyledTriggerButton = styled(Button)(() => ({
  'height': '26px',
  'padding': '0 8px',
  '.MuiTouchRipple-child': {
    backgroundColor: `${designSystemToken('semantic.bg.brand')} !important`,
  },
}));

function getScopeLabel(segment: Segment, currentUserId: string | null): string {
  if (isCampaignSegment(segment)) {
    return translate('Campaign');
  }
  const scope = segment.scope ?? SEGMENT_SCOPE.BRAND;
  const label = translate(SEGMENT_SCOPE_SHORT_LABEL[scope]);
  const isOwned =
    currentUserId != null && segment.createdByDashboardUserId === currentUserId;
  if (!isOwned) {
    return `${label} · ${translate('Shared with you')}`;
  }
  return label;
}

const SEGMENTS_MENU_WIDTH = 340;
const SEGMENTS_MENU_MAX_HEIGHT = 400;

function EmptyState({isLoadingSegments}: {isLoadingSegments: boolean}) {
  return (
    <div className={styles.emptyState}>
      <Body
        size="sm"
        color={designSystemToken('semantic.fg.inverse-secondary')}
      >
        {translate(isLoadingSegments ? 'Loading...' : 'No saved filters yet')}
      </Body>
    </div>
  );
}

function SegmentsButton({
  segments,
  activeSegment,
  activeCampaignSegment,
  isLoadingSegments,
  onSegmentDeleted,
  onSelectSegment,
  onOpen,
}: SegmentsButtonProps) {
  const brandId = useBrandId();
  const deleteMutation = useDeleteSegmentMutation();
  const {dashboardUser} = useDashboardUserQuery({});
  const currentUserId = dashboardUser?.id ?? null;

  const [segmentPendingDelete, setSegmentPendingDelete] =
    useState<Segment | null>(null);

  const activeDisplay = activeSegment ?? activeCampaignSegment ?? null;
  const isActive = activeDisplay != null;
  const activeId = activeSegment?.id ?? activeCampaignSegment?.id ?? null;

  const handleDeleteSegment = useCallback(
    async (segment: Segment) => {
      if (!brandId) {
        return;
      }
      try {
        await deleteMutation.mutateAsync({
          id: segment.id,
          merchantId: brandId,
        });
        onSegmentDeleted?.(segment);
      } catch {
        // Error surfaced by mutation
      }
    },
    [brandId, deleteMutation, onSegmentDeleted]
  );

  const handleConfirmDelete = async () => {
    if (segmentPendingDelete) {
      await handleDeleteSegment(segmentPendingDelete);
      setSegmentPendingDelete(null);
    }
  };

  const canDeleteSegment = useCallback(
    (segment: Segment): boolean =>
      !isCampaignSegment(segment) &&
      Boolean(
        currentUserId && segment.createdByDashboardUserId === currentUserId
      ),
    [currentUserId]
  );

  const options: DropdownOption[] = useMemo(() => {
    const saved: Segment[] = [];
    const campaign: Segment[] = [];
    segments.forEach((s) => (isCampaignSegment(s) ? campaign : saved).push(s));
    const sortedSaved = [...saved].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    const ordered = [...sortedSaved, ...campaign];

    return ordered.map((s) => {
      const isSegmentActive = s.id === activeId;
      return {
        id: s.id,
        label: s.name,
        subtitle: getScopeLabel(s, currentUserId),
        badge: isSegmentActive ? translate('Active') : undefined,
        badgeStyle: isSegmentActive
          ? {
              backgroundColor: designSystemToken('semantic.bg.brand-secondary'),
              textColor: designSystemToken('semantic.fg.brand-secondary'),
            }
          : undefined,
        endIcon: canDeleteSegment(s) ? (
          <IconButton
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              setSegmentPendingDelete(s);
            }}
            aria-label={translate('Delete saved filter')}
            className={styles.deleteIcon}
          >
            <Icon
              name="Actions-Remove"
              size="medium"
              appearance="neutral"
              color={designSystemToken('semantic.fg.secondary')}
            />
          </IconButton>
        ) : undefined,
      };
    });
  }, [segments, activeId, canDeleteSegment, currentUserId]);

  const handleChange = useCallback(
    (option: DropdownOption) => {
      if (option.id === activeSegment?.id) {
        return;
      }
      const segment = segments.find((s) => s.id === option.id);
      if (segment) {
        onSelectSegment(segment);
      }
    },
    [activeSegment?.id, segments, onSelectSegment]
  );

  const selectedIds = useMemo(() => (activeId ? [activeId] : []), [activeId]);

  return (
    <>
      <Modal
        open={segmentPendingDelete != null}
        handleClose={() => setSegmentPendingDelete(null)}
        showCloseButton
        contentClassName={styles.deleteConfirmModalBox}
      >
        <div className={styles.deleteConfirmContainer}>
          <Headline size="md" className={styles.deleteConfirmTitle}>
            {translate('Delete saved filter?')}
          </Headline>
          <Body size="lg" className={styles.deleteConfirmBody}>
            {segmentPendingDelete ? (
              <>
                <span style={{fontWeight: 500}}>
                  {segmentPendingDelete.name}
                </span>{' '}
                {translate('will be permanently deleted.')}
              </>
            ) : null}
          </Body>
          <div className={styles.deleteConfirmActions}>
            <Button
              size="medium"
              appearance="neutral"
              mode="tinted"
              onClick={() => setSegmentPendingDelete(null)}
            >
              {translate('Cancel')}
            </Button>
            <Button
              size="medium"
              appearance="danger"
              mode="filled"
              onClick={handleConfirmDelete}
            >
              {translate('Delete')}
            </Button>
          </div>
        </div>
      </Modal>
      <Dropdown
        label={activeDisplay?.name || translate('Saved Filters')}
        title={translate('Saved filters')}
        options={isLoadingSegments ? [] : options}
        selectedIds={selectedIds}
        onChange={handleChange}
        selectionMode="single"
        menuWidth={SEGMENTS_MENU_WIDTH}
        menuMaxHeight={SEGMENTS_MENU_MAX_HEIGHT}
        emptyState={<EmptyState isLoadingSegments={isLoadingSegments} />}
        hideMenuScrollbar
        shouldScrollToSelectedItem
        renderTrigger={({onClick, isOpen}) => (
          <StyledTriggerButton
            testID="segments-button"
            size="small"
            variant="text"
            onClick={(e) => {
              onClick(e);
              if (!isOpen) {
                onOpen?.();
              }
            }}
            startIcon={
              <Icon
                name="Actions-Bookmark"
                size="small"
                appearance={isActive ? 'brand' : 'neutral'}
                color={designSystemToken('semantic.fg.secondary')}
                style={{width: '18px', height: '18px'}}
              />
            }
            endIcon={
              <AnimatedCaret
                isOpen={isOpen}
                appearance={isActive ? 'brand' : 'neutral'}
                size={18}
              />
            }
          >
            <Body
              size="sm"
              color={
                isActive
                  ? designSystemToken('semantic.fg.brand-secondary')
                  : designSystemToken('semantic.fg.primary')
              }
            >
              {activeDisplay?.name || translate('Saved Filters')}
            </Body>
          </StyledTriggerButton>
        )}
      />
    </>
  );
}

export default SegmentsButton;
